import { Dispatch, SetStateAction, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Message } from 'primereact/message'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// assets
import successImage from 'assets/images/success-image.svg'
import loadingCalendar from 'assets/icons/loading-calendar.png'

// local imports
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import NotificationDialog from 'components/ui/NotificationDialog'
import { ISideBarModel } from 'types/CalendarType'
import Loader from 'components/ui/Loader'
import FormModal from 'components/ui/modal/FormModal'
import PoolVehicleSearchForm from './PoolVehicleSearchForm'
import { SearchModalHeader } from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'
import PoolVehicleResModalWrapper from './PoolVehicleResModal'
import { IPoolVehicleSearch } from 'types/PoolVehicleType'
import useGetPoolVehicleList from 'components/home/on-campus-services/pool-vehicles/hooks/use-get-pool-vehicles-list'
import usePoolVehicleResModal from 'components/home/on-campus-services/pool-vehicles/hooks/use-pool-vehicles-res-modal'
import useGetPoolVehicleLocationList from 'components/home/on-campus-services/pool-vehicles/hooks/use-get-pool-vehicles-locations'

interface ModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeVehicleModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function PoolVehicleSearchModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeVehicleModal,
  sidebarAfterSave,
}: ModalProps) {
  const { t } = useTranslation()
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId

  /* Filter form default values */
  const defaultValues: IPoolVehicleSearch = {
    locationId: employeeLocation ?? 0,
    vehicle: null,
    fuelTypes: [],
    gearType: 0,
    reservationDate: selectedDayDate,
  }
  const filteredDate = defaultValues.reservationDate

  /* Filter form state  */
  const { watch, resetField, control } = useForm<IPoolVehicleSearch>({
    defaultValues,
  })

  /* Get pool vehicle search form values */
  const locationId = watch('locationId')
  const fuelType = watch('fuelTypes')
  const gearType = watch('gearType')
  const _selectedVehicle = watch('vehicle')

  /* Get Pool Vehicle List - Locations | Custom Hooks */
  const { getPoolVehiclesList, vehicles, loading } = useGetPoolVehicleList()

  const { data: locationList, fuelTypes, gearTypes, isLoading: locationLoading } = useGetPoolVehicleLocationList()

  /* Get Pool Vehicle Room List on initial page load  */
  useEffect(() => {
    getPoolVehiclesList({
      locationId: locationId,
      reservationDate: filteredDate,
      fuelTypeIds: fuelType,
      gearTypeId: gearType,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, fuelType, gearType])

  /* Reservation Modal (Open - Close, Selected Pool Vehicle Room). | Custom Hook */
  const {
    openModal,
    closeModal,
    modal: isOpen,
    selectedVehicle,
    notification,
    setNotification,
  } = usePoolVehicleResModal()

  /* Search Modal Methods */
  const handleOnCancel = () => closeVehicleModal()
  const selectRoomAndOpen = () => {
    if (!!_selectedVehicle) {
      openModal(_selectedVehicle)
      setModal(false)
    }
  }

  /* Reservation Scheduler Modal */
  const handleOnCancelReservationModal = () => {
    closeModal()
    setModal(true)
  }

  /* Notification Modal */
  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  // past date reservation control - warning text
  const isBeforeToday = DateUtil.IsPastReservations(filteredDate)

  return (
    <>
      <FormModal
        id="poolVehicleSelectionModal"
        closable
        header={<span>{t('pool-vehicle-res-modal.pool-vehicle-reservation')} (1/2)</span>}
        disabled={!_selectedVehicle}
        onSave={selectRoomAndOpen}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}>
        <SearchModalHeader style={{ marginBottom: '8px' }}>
          <div className="dialog-subheader">
            <span>{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
            {isBeforeToday && (
              <Message
                severity="warn"
                style={{ borderRadius: '10px', fontWeight: 600, marginBottom: '0.5rem' }}
                text={t('pool-vehicle-res-modal.warning-text')}
              />
            )}
          </div>
        </SearchModalHeader>

        <PoolVehicleSearchForm
          control={control}
          resetField={resetField}
          vehicleList={vehicles}
          locationList={locationList}
          gearTypes={gearTypes}
          fuelTypes={fuelTypes}
          isDisabled={isBeforeToday || loading || locationLoading}
        />

        {!isBeforeToday && (
          <>
            {loading || locationLoading ? (
              <div className="my-3 w-100">
                <Loader src={loadingCalendar} />
              </div>
            ) : null}
          </>
        )}
      </FormModal>

      {/* Pool Vehicle Res. Scheduler Modal */}
      {!!selectedVehicle ? (
        <PoolVehicleResModalWrapper
          selectedVehicle={selectedVehicle}
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          cancelReservationModal={handleOnCancelReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          filterDate={filteredDate}
          selectedDateStr={selectedDateStr}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('pool-vehicle-res-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
